import axios, { AxiosInstance, AxiosRequestConfig, AxiosError, InternalAxiosRequestConfig } from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

// Public endpoints that don't require authentication
const PUBLIC_PATHS = ['/auth/', '/health', '/billing/plans', '/dev/'];

// Check if a path is public (doesn't require auth)
const isPublicPath = (url: string | undefined): boolean => {
  if (!url) return false;
  return PUBLIC_PATHS.some(path => url.includes(path));
};

class ApiService {
  private client: AxiosInstance;
  private isRefreshing: boolean = false;
  private refreshSubscribers: Array<{
    resolve: (token: string) => void;
    reject: (error: Error) => void;
  }> = [];

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
        'X-API-Version': '1',
      },
    });

    this.setupRequestInterceptor();
    this.setupResponseInterceptor();
  }

  /**
   * Request Interceptor
   * - Adds Bearer token to all requests if available
   * - Allows public paths without token
   */
  private setupRequestInterceptor(): void {
    this.client.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        const accessToken = localStorage.getItem('access_token');

        if (accessToken) {
          config.headers.Authorization = `Bearer ${accessToken}`;
        } else if (!isPublicPath(config.url)) {
          // No token and not a public path - reject request
          return Promise.reject(new Error('No access token available'));
        }

        // For FormData, let the browser set Content-Type with boundary
        // Don't override it with application/json
        if (config.data instanceof FormData) {
          delete config.headers['Content-Type'];
        }

        return config;
      },
      (error) => Promise.reject(error)
    );
  }

  /**
   * Response Interceptor
   * - On 401: Try to refresh token
   * - On refresh success: Retry original request
   * - On refresh failure: Clear tokens and redirect to login
   */
  private setupResponseInterceptor(): void {
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

        // Only handle 401 errors
        if (error.response?.status !== 401) {
          return Promise.reject(error);
        }

        console.log(`[ApiService] 401 received for ${originalRequest.url}`);

        // Don't refresh for auth endpoints (login, register, refresh itself)
        if (isPublicPath(originalRequest.url)) {
          console.log('[ApiService] Skipping refresh for public path');
          return Promise.reject(error);
        }

        // Already retried - don't loop
        if (originalRequest._retry) {
          console.log('[ApiService] Already retried, redirecting to login');
          this.handleAuthFailure();
          return Promise.reject(error);
        }

        originalRequest._retry = true;

        // If already refreshing, wait for the refresh to complete
        if (this.isRefreshing) {
          console.log('[ApiService] Already refreshing, queueing request');
          return new Promise((resolve, reject) => {
            this.refreshSubscribers.push({
              resolve: (newToken: string) => {
                if (originalRequest.headers) {
                  originalRequest.headers.Authorization = `Bearer ${newToken}`;
                }
                resolve(this.client(originalRequest));
              },
              reject: (error: Error) => {
                reject(error);
              },
            });
          });
        }

        // Start refreshing
        console.log('[ApiService] Starting token refresh...');
        this.isRefreshing = true;

        try {
          const newAccessToken = await this.refreshAccessToken();
          
          // Update the original request with new token
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          }

          // Notify all subscribers waiting for the refresh
          this.onRefreshSuccess(newAccessToken);

          // Retry the original request
          console.log(`[ApiService] Retrying original request: ${originalRequest.url}`);
          return this.client(originalRequest);
        } catch (refreshError) {
          // Refresh failed - redirect to login
          console.error('[ApiService] Token refresh failed:', refreshError);
          this.onRefreshFailure(refreshError as Error);
          this.handleAuthFailure();
          return Promise.reject(refreshError);
        } finally {
          this.isRefreshing = false;
        }
      }
    );
  }

  /**
   * Refresh the access token using the refresh token
   */
  private async refreshAccessToken(): Promise<string> {
    const refreshToken = localStorage.getItem('refresh_token');

    if (!refreshToken) {
      console.warn('[ApiService] No refresh token available');
      throw new Error('No refresh token available');
    }

    console.log('[ApiService] Attempting to refresh access token...');

    // Use axios directly to avoid interceptor loops
    const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
      refreshToken,
    });

    const { accessToken, refreshToken: newRefreshToken } = response.data.data;

    console.log('[ApiService] Token refresh successful');

    // Save new tokens
    localStorage.setItem('access_token', accessToken);
    if (newRefreshToken) {
      localStorage.setItem('refresh_token', newRefreshToken);
    }

    return accessToken;
  }

  /**
   * Notify all waiting requests that refresh succeeded
   */
  private onRefreshSuccess(newToken: string): void {
    this.refreshSubscribers.forEach((subscriber) => subscriber.resolve(newToken));
    this.refreshSubscribers = [];
  }

  /**
   * Notify all waiting requests that refresh failed
   */
  private onRefreshFailure(error: Error): void {
    this.refreshSubscribers.forEach((subscriber) => subscriber.reject(error));
    this.refreshSubscribers = [];
  }

  /**
   * Handle authentication failure - clear tokens and redirect to login
   */
  private handleAuthFailure(): void {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    
    // Only redirect if not already on login page
    if (!window.location.pathname.includes('/auth') && window.location.pathname !== '/') {
      window.location.href = '/';
    }
  }

  // ==================== HTTP Methods ====================

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.get(url, config);
    return response.data.data;
  }

  async post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.post(url, data, config);
    return response.data.data;
  }

  async put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.put(url, data, config);
    return response.data.data;
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.delete(url, config);
    return response.data.data;
  }

  async patch<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.patch(url, data, config);
    return response.data.data;
  }
}

export const apiService = new ApiService();
export default apiService;
